import i18next from "i18next";
import { initReactI18next } from "react-i18next";

const LOCALE_STORAGE_KEY = "locale";
const SUPPORTED_LOCALES = ["en", "fr", "de"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: "EN",
  fr: "FR",
  de: "DE",
};

function getSavedLocale(): SupportedLocale {
  const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (saved && SUPPORTED_LOCALES.includes(saved as SupportedLocale)) {
    return saved as SupportedLocale;
  }
  return "en";
}

async function fetchBundle(locale: SupportedLocale): Promise<Record<string, string>> {
  const res = await fetch(`/api/translations?locale=${locale}`);
  if (!res.ok) throw new Error(`Failed to fetch translations for locale: ${locale}`);
  return res.json();
}

export async function initI18n(): Promise<void> {
  const locale = getSavedLocale();
  const bundle = await fetchBundle(locale);

  await i18next.use(initReactI18next).init({
    lng: locale,
    fallbackLng: "en",
    interpolation: { escapeValue: false },
    resources: {
      [locale]: { translation: bundle },
    },
  });

  i18next.on("languageChanged", (lng) => {
    localStorage.setItem(LOCALE_STORAGE_KEY, lng);
  });
}

/**
 * Loads translations for the given locale (fetches only if not already cached),
 * then switches the active language. The locale is persisted via the languageChanged listener.
 */
export async function loadAndSwitch(locale: SupportedLocale): Promise<void> {
  if (!i18next.hasResourceBundle(locale, "translation")) {
    const bundle = await fetchBundle(locale);
    i18next.addResourceBundle(locale, "translation", bundle, true, true);
  }
  await i18next.changeLanguage(locale);
}

export default i18next;
